import { TypedPolicy } from '@/react/portainer/policies/types';

import { Badge } from '@@/Badge';
import { FormSection } from '@@/form-components/FormSection';

import { useChangeConfirmationInitialValues } from './useChangeConfirmationInitialValues';
import { ConfirmSeverity } from './types';

interface Props {
  policy: TypedPolicy<unknown>;
}

export function ChangeConfirmationPolicyReadOnly({ policy }: Props) {
  const values = useChangeConfirmationInitialValues(policy);

  return (
    <>
      <FormSection title="Environment highlight">
        <div className="rounded-lg border border-gray-5 th-dark:border-gray-7 overflow-hidden">
          <BooleanItem
            label="Highlight environment"
            value={values.highlightEnabled}
          />
          {values.highlightEnabled && (
            <>
              <ColorItem
                label="Highlight color"
                color={values.highlightColor ?? ''}
              />
              <TextItem
                label="Banner message"
                value={values.bannerMessage || '—'}
              />
            </>
          )}
        </div>
      </FormSection>

      <FormSection title="Change confirmation">
        <div className="rounded-lg border border-gray-5 th-dark:border-gray-7 overflow-hidden">
          <BooleanItem
            label="Enable change confirmation"
            value={values.confirmEnabled}
          />
          {values.confirmEnabled && (
            <>
              <TextItem
                label="Confirmation title"
                value={values.confirmTitle || '—'}
              />
              <TextItem
                label="Confirmation message"
                value={values.confirmMessage || '—'}
              />
              <SeverityItem label="Severity" value={values.confirmSeverity} />
            </>
          )}
        </div>
      </FormSection>
    </>
  );
}

function BooleanItem({
  label,
  value,
}: {
  label: string;
  value: boolean | undefined;
}) {
  return (
    <div className="flex items-center justify-between py-3 px-4 bg-gray-2 th-dark:bg-gray-iron-10 border-b border-gray-5 th-dark:border-gray-7 last:border-b-0">
      <span className="text-sm">{label}</span>
      <Badge type={value ? 'success' : 'muted'}>
        {value ? 'Enabled' : 'Disabled'}
      </Badge>
    </div>
  );
}

function TextItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-3 px-4 bg-gray-2 th-dark:bg-gray-iron-10 border-b border-gray-5 th-dark:border-gray-7 last:border-b-0">
      <span className="text-sm">{label}</span>
      <span className="text-sm text-muted">{value}</span>
    </div>
  );
}

function ColorItem({ label, color }: { label: string; color: string }) {
  return (
    <div className="flex items-center justify-between py-3 px-4 bg-gray-2 th-dark:bg-gray-iron-10 border-b border-gray-5 th-dark:border-gray-7 last:border-b-0">
      <span className="text-sm">{label}</span>
      <div className="flex items-center gap-2">
        <div
          className="w-4 h-4 rounded border border-gray-5 th-dark:border-gray-7"
          style={{ backgroundColor: color }}
        />
        <span className="text-sm text-muted font-mono">{color}</span>
      </div>
    </div>
  );
}

const SEVERITY_LABELS: Record<ConfirmSeverity, string> = {
  default: 'Default',
  warning: 'Warning',
  error: 'Error',
};

function SeverityItem({
  label,
  value,
}: {
  label: string;
  value: ConfirmSeverity;
}) {
  return (
    <div className="flex items-center justify-between py-3 px-4 bg-gray-2 th-dark:bg-gray-iron-10 border-b border-gray-5 th-dark:border-gray-7 last:border-b-0">
      <span className="text-sm">{label}</span>
      <span className="text-sm text-muted">{SEVERITY_LABELS[value]}</span>
    </div>
  );
}
